import React from 'react';
import LineItemsList, { type LineColumn } from '../ui/LineItemsList';
import { formatDecimal } from '../../utils/decimal';
import { lineTotal, type DraftLine } from '../../utils/bookingLines';

interface Props {
  lines: readonly DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  errors: Readonly<Record<string, string>>;
  /** Read-only: a viewer, or an archived booking. */
  disabled: boolean;
  /** Prices and totals are left out entirely for someone who may not see money. */
  canViewFinancials: boolean;
  currency: string | null;
  /** Source of the next line key. */
  nextKey: () => string;
}

/** The booking's priced lines, edited as a list and saved with the page (the booking's total is their sum). */
const BookingLines: React.FC<Props> = ({ lines, onChange, errors, disabled, canViewFinancials, currency, nextKey }) => {
  const patch = (key: string, change: Partial<DraftLine>) => onChange(lines.map(l => (l.key === key ? { ...l, ...change } : l)));

  const columns: LineColumn<DraftLine>[] = [
    {
      key: 'description', header: 'Description', wide: true,
      render: (line, ctx) => (
        <input
          type="text" maxLength={2000} value={line.description} aria-label={ctx.ariaLabel} aria-describedby={ctx.errorId} aria-invalid={!!ctx.errorId}
          disabled={ctx.disabled} onChange={e => patch(line.key, { description: e.target.value })}
        />
      ),
    },
    {
      key: 'quantity', header: 'Quantity',
      render: (line, ctx) => (
        <input
          type="text" inputMode="decimal" value={line.quantity} aria-label={ctx.ariaLabel} aria-describedby={ctx.errorId}
          disabled={ctx.disabled || !canViewFinancials} onChange={e => patch(line.key, { quantity: e.target.value })}
        />
      ),
    },
    ...(canViewFinancials ? [
      {
        key: 'unit_price', header: 'Unit price',
        render: (line: DraftLine, ctx: { ariaLabel: string; errorId: string | undefined; disabled: boolean }) => (
          <input
            type="text" inputMode="decimal" value={line.unit_price} aria-label={ctx.ariaLabel} aria-describedby={ctx.errorId}
            disabled={ctx.disabled} onChange={e => patch(line.key, { unit_price: e.target.value })}
          />
        ),
      },
      {
        key: 'total', header: 'Line total',
        render: (line: DraftLine) => <span className="vb-line-total">{formatDecimal(lineTotal(line.quantity, line.unit_price), currency)}</span>,
      },
    ] : []),
  ];

  return (
    <LineItemsList<DraftLine>
      label="Booking lines"
      lines={lines}
      getKey={l => l.key}
      lineName={l => l.description}
      columns={columns}
      errors={errors}
      disabled={disabled}
      addLabel="Add line"
      onAdd={canViewFinancials ? () => onChange([...lines, { key: nextKey(), description: '', quantity: '1', unit_price: '' }]) : undefined}
      onRemove={canViewFinancials ? line => onChange(lines.filter(l => l.key !== line.key)) : undefined}
      emptyTitle="No lines yet"
      emptyHint={disabled ? undefined : 'Add a line for each thing this vendor is booked to provide. With lines, the total is their sum.'}
    />
  );
};

export default BookingLines;
